<template>
  <div>
    <!-- Drop Zone -->
    <v-card
      outlined
      class="mb-4 pa-6 text-center drop-zone"
      :class="{ 'drop-zone--active': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <v-icon size="48" color="grey lighten-1">mdi-cloud-upload</v-icon>
      <div class="mt-2 grey--text">Drag &amp; drop XLSX files here, or</div>
      <v-btn color="primary" class="mt-2" @click="$refs.fileInput.click()">
        Select Files
      </v-btn>
      <input
        ref="fileInput"
        type="file"
        multiple
        accept=".xlsx,.xls"
        style="display: none"
        @change="onFileSelect"
      />
    </v-card>

    <!-- File List Table -->
    <v-simple-table v-if="uploadedFiles.length" dense class="mb-4">
      <thead>
        <tr>
          <th>Filename</th>
          <th>Detected Type</th>
          <th style="min-width: 220px">Assigned Type</th>
          <th>Confidence</th>
          <th style="width: 50px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in uploadedFiles" :key="entry.id">
          <td>{{ entry.filename }}</td>
          <td>
            <v-chip
              v-if="entry.suggestedType"
              small
              :color="entry.confidence === 'high' ? 'green' : 'orange'"
              text-color="white"
            >
              {{ fileTypeLabel(entry.suggestedType) }}
            </v-chip>
            <span v-else class="grey--text text--lighten-1">--</span>
          </td>
          <td>
            <v-select
              v-model="entry.assignedType"
              :items="availableTypes(entry.id)"
              item-text="text"
              item-value="value"
              dense
              hide-details
              clearable
              placeholder="Select type..."
              :loading="entry.sheetsLoading"
            ></v-select>
          </td>
          <td>
            <span v-if="entry.confidence" :class="entry.confidence === 'high' ? 'green--text' : 'orange--text'">
              {{ entry.confidence }}
            </span>
          </td>
          <td>
            <v-btn icon small @click="removeFile(entry.id)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Duplicate type warning -->
    <v-alert v-if="duplicateTypes.length" type="warning" dense class="mb-4">
      Duplicate type assignments: {{ duplicateTypes.join(', ') }}. Each type can only be assigned to one file.
    </v-alert>

    <!-- Validate All button -->
    <div v-if="uploadedFiles.length" class="mb-4">
      <v-btn
        color="primary"
        :disabled="!canValidate"
        :loading="isValidating"
        @click="validateAll"
      >
        <v-icon left>mdi-check-all</v-icon>
        Validate All
      </v-btn>
      <span v-if="!canValidate && uploadedFiles.length" class="ml-3 grey--text text-caption">
        Assign a unique type to each file to enable validation.
      </span>
    </div>

    <!-- Validation Progress -->
    <div v-if="validationJobId">
      <v-progress-linear
        v-model="uploadProgress"
        color="light-blue"
        height="25"
        reactive
      >
        <strong>{{ uploadProgress }} %</strong>
      </v-progress-linear>
    </div>

    <v-alert
      v-if="validationJob && (validationJob.status === 'queued' || validationJob.status === 'running')"
      border="left"
      color="indigo"
      dark
      class="mb-4"
    >
      <div><strong>{{ validationJob.message || 'Validation in progress' }}</strong></div>
      <div>
        Job: {{ validationJobId }} |
        Progress: {{ validationJob.progress ? validationJob.progress.completed_sheets : 0 }}/{{ validationJob.progress ? validationJob.progress.total_sheets : 0 }} sheets
        ({{ validationJob.progress_pct || 0 }}%)
      </div>
      <div v-if="validationJob.progress && validationJob.progress.current_sheet">
        Current: {{ validationJob.progress.current_file || '-' }} / {{ validationJob.progress.current_sheet }}
      </div>
      <div>Auto-refresh: every 5 seconds</div>
    </v-alert>

    <v-alert v-if="message" border="left" color="blue-grey" dark class="mb-4">
      <pre>{{ message }}</pre>
    </v-alert>

    <!-- Validation Result Summary -->
    <v-alert
      v-if="validationResult"
      border="left"
      :color="statusColor(validationResult.status)"
      dark
      class="mb-4"
    >
      <div><strong>{{ validationResult.message }}</strong></div>
      <div>
        Status: {{ validationResult.status }} |
        Errors: {{ validationResult.total_errors }} |
        Groups: {{ validationResult.total_groups }}
      </div>
      <div v-if="validationResult.truncated">
        Showing sample rows only (truncated).
      </div>
    </v-alert>

    <!-- Validation Details -->
    <v-card
      v-if="validationResult && validationResult.files && validationResult.files.length"
      class="mb-4"
      outlined
    >
      <v-card-title>Validation Details</v-card-title>
      <v-card-text>
        <div
          v-for="fileResult in validationResult.files"
          :key="fileResult.file_key"
          class="mb-6"
        >
          <div class="font-weight-bold">
            {{ fileResult.file_name || fileResult.file_key }}
          </div>
          <div class="text--secondary mb-2">
            Status: {{ fileResult.status }} |
            Errors: {{ fileResult.total_errors }} |
            Groups: {{ fileResult.total_groups }}
          </div>

          <v-simple-table
            dense
            v-if="fileResult.error_groups && fileResult.error_groups.length"
          >
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Rule</th>
                <th>Column</th>
                <th>Count</th>
                <th>Sample rows</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(group, index) in fileResult.error_groups"
                :key="`${fileResult.file_key}-${group.code}-${group.column}-${index}`"
              >
                <td>{{ group.sheet_name || '-' }}</td>
                <td>{{ group.message }}</td>
                <td>{{ group.column || '-' }}</td>
                <td>{{ group.count }}</td>
                <td>{{ formatExamples(group.examples) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Create Planning Section -->
    <v-card
      v-if="canCreatePlanning"
      outlined
      class="mb-4"
    >
      <v-card-title>
        <v-icon left>mdi-plus-circle</v-icon>
        Create Planning from Validated Files
      </v-card-title>
      <v-card-text>
        <v-form ref="planningForm">
          <v-container>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="planningInfo"
                  :rules="[rules.required]"
                  label="Planning Info"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  prepend-icon="mdi-human"
                  v-model="username"
                  :rules="[rules.required]"
                  :items="entityOptions"
                  item-text="option"
                  item-value="value"
                  label="Author"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-menu
                  v-model="datepicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="date"
                      label="Planning Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    landscape
                    type="month"
                    v-model="date"
                    @input="datepicker = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  prepend-icon="mdi-cash"
                  v-model="lcyOption"
                  :rules="[rules.required]"
                  :items="currencyOptions"
                  item-text="option"
                  item-value="id"
                  label="Currency"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="sales_floor_id"
                  :rules="[rules.required]"
                  :items="salesFloorOptions"
                  item-text="option"
                  item-value="id"
                  label="Sales IR Floor"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="funding_floor_id"
                  :rules="[rules.required]"
                  :items="fundingFloorOptions"
                  item-text="option"
                  item-value="id"
                  label="Funding IR Floor"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="sales_down_payment_id"
                  :rules="[rules.required]"
                  :items="salesDownPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Down Payment"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="payment_choice_for_funding"
                  :rules="[rules.required]"
                  :items="paymentChoiceForFundingOptions"
                  item-text="option"
                  item-value="value"
                  label="Funding Payment Timing"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="other_funding_input"
                  :items="otherFundingInputOptions"
                  item-text="option"
                  item-value="value"
                  label="Other Funding Input"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="fee_calculation_ol"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Fee Calc OL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="rent_calculation_ol"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Rent Calc OL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="depo_calculation_ol"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Depo Calc OL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="fee_calculation_fl"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Fee Calc FL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="rent_calculation_fl"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Rent Calc FL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="depo_calculation_fl"
                  :rules="[rules.required]"
                  :items="calculationPaymentOptions"
                  item-text="option"
                  item-value="id"
                  label="Depo Calc FL"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="leasing_or_rent"
                  :items="leasingOrRentOptions"
                  item-text="option"
                  item-value="value"
                  label="Leasing or Rent"
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="accent"
          large
          :loading="creatingPlanning"
          @click="createPlanning"
        >
          <v-icon left>mdi-content-save</v-icon>
          Create Planning
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="-1" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import store from '../store/index';
import api from '../api/lbt-api';
import {
  FILE_TYPES, detectByFilename, detectBySheetNames, isValidatedType,
} from '../utilities/fileTypeDetection';

const {
  createNewPlanning, calcPlanning, replacePlanning, inspectSheets,
} = api;

let nextId = 1;

export default {
  name: 'multi-file-upload',

  data() {
    return {
      isDragging: false,
      uploadedFiles: [],
      // Validation
      validationJobId: null,
      validationJob: null,
      validationResult: null,
      pollingTimer: null,
      uploadProgress: 0,
      message: '',
      isValidating: false,
      // Planning form
      creatingPlanning: false,
      datepicker: false,
      planningInfo: '',
      username: '',
      date: new Date().toISOString().substr(0, 7),
      lcyOption: '',
      sales_floor_id: 0,
      funding_floor_id: 0,
      sales_down_payment_id: 0,
      fee_calculation_ol: 0,
      fee_calculation_fl: 0,
      rent_calculation_ol: 0,
      rent_calculation_fl: 0,
      depo_calculation_ol: 0,
      depo_calculation_fl: 0,
      other_funding_input: '2030320000',
      payment_choice_for_funding: '0',
      leasing_or_rent: 'none',
      // Options
      currencyOptions: [],
      entityOptions: [],
      salesFloorOptions: [],
      fundingFloorOptions: [],
      salesDownPaymentOptions: [],
      calculationPaymentOptions: [],
      leasingOrRentOptions: [],
      paymentChoiceForFundingOptions: [],
      otherFundingInputOptions: [
        { option: 'Deposits from customers (2030320000)', value: '2030320000' },
        { option: 'Deposits from banks (2030310000)', value: '2030310000' },
      ],
      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      // Rules
      rules: {
        required: v => (v !== '' && v !== null && v !== undefined) || 'Required',
      },
    };
  },

  computed: {
    ...mapGetters('options', ['getOptionCategory']),

    duplicateTypes() {
      const counts = {};
      this.uploadedFiles.forEach((f) => {
        if (f.assignedType) {
          counts[f.assignedType] = (counts[f.assignedType] || 0) + 1;
        }
      });
      return Object.entries(counts)
        .filter(([, count]) => count > 1)
        .map(([type]) => this.fileTypeLabel(type));
    },

    canValidate() {
      if (!this.uploadedFiles.length) return false;
      if (this.duplicateTypes.length) return false;
      // Every file must have a type assigned
      const allAssigned = this.uploadedFiles.every(f => f.assignedType);
      if (!allAssigned) return false;
      // At least one file must be a validated type
      return this.uploadedFiles.some(f => isValidatedType(f.assignedType));
    },

    canCreatePlanning() {
      if (!this.validationResult) return false;
      if (this.validationResult.status !== 'ok') return false;
      // All validated files passed
      const validatedFiles = this.uploadedFiles.filter(f => isValidatedType(f.assignedType));
      if (!validatedFiles.length) return false;
      return this.validationResult.files && this.validationResult.files.every(f => f.status === 'ok');
    },
  },

  beforeDestroy() {
    this.clearPolling();
  },

  async beforeRouteEnter(from, to, next) {
    await store.dispatch('options/getOptions');
    next();
  },

  async created() {
    await store.dispatch('options/getOptions');
    this.loadOptions();
  },

  methods: {
    loadOptions() {
      const cat = this.getOptionCategory;
      if (cat('CURRENCY')) this.currencyOptions = cat('CURRENCY').options;
      if (cat('LEGAL_ENTITY')) this.entityOptions = cat('LEGAL_ENTITY').options;
      if (cat('YES_NO_OPTION_FLOOR')) {
        this.salesFloorOptions = cat('YES_NO_OPTION_FLOOR').options;
        this.fundingFloorOptions = cat('YES_NO_OPTION_FLOOR').options;
      }
      if (cat('DOWN_PAYMENT_FINREP_POSITION')) this.salesDownPaymentOptions = cat('DOWN_PAYMENT_FINREP_POSITION').options;
      if (cat('CALC_TYPE')) this.calculationPaymentOptions = cat('CALC_TYPE').options;
      if (cat('LEASING_OR_RENT')) this.leasingOrRentOptions = cat('LEASING_OR_RENT').options;
      if (cat('PAYMENT_CHOICE_FOR_FUNDING')) this.paymentChoiceForFundingOptions = cat('PAYMENT_CHOICE_FOR_FUNDING').options;
      if (!this.paymentChoiceForFundingOptions.length) {
        this.paymentChoiceForFundingOptions = [
          { option: 'After', value: '0' },
          { option: 'Before', value: '1' },
        ];
      }
    },

    // --- File handling ---

    onDrop(event) {
      this.isDragging = false;
      const files = Array.from(event.dataTransfer.files).filter(f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls'));
      this.addFiles(files);
    },

    onFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      this.$refs.fileInput.value = '';
    },

    addFiles(files) {
      // Reset validation state when new files are added
      this.validationResult = null;
      this.validationJobId = null;
      this.validationJob = null;
      this.message = '';
      this.clearPolling();

      const newEntries = files.map((file) => {
        const detection = detectByFilename(file.name);
        const id = nextId;
        nextId += 1;
        return {
          id,
          file,
          filename: file.name,
          suggestedType: detection ? detection.type : null,
          assignedType: detection ? detection.type : null,
          confidence: detection ? detection.confidence : null,
          sheetNames: [],
          sheetsLoading: false,
        };
      });

      this.uploadedFiles.push(...newEntries);

      // For files not detected by filename, try sheet-name detection
      const unmatched = newEntries.filter(e => !e.suggestedType);
      if (unmatched.length) {
        this.inspectUnmatchedFiles(unmatched);
      }
    },

    async inspectUnmatchedFiles(entries) {
      entries.forEach((e) => { this.$set(e, 'sheetsLoading', true); });
      try {
        const result = await inspectSheets(entries.map(e => e.file));
        if (result && result.files) {
          result.files.forEach((fileInfo, idx) => {
            const entry = entries[idx];
            if (!entry) return;
            this.$set(entry, 'sheetNames', fileInfo.sheets || []);
            this.$set(entry, 'sheetsLoading', false);

            const detection = detectBySheetNames(entry.sheetNames);
            if (detection) {
              this.$set(entry, 'suggestedType', detection.type);
              this.$set(entry, 'confidence', detection.confidence);
              // Only auto-assign if no duplicate
              const alreadyUsed = this.uploadedFiles.some(f => f.id !== entry.id && f.assignedType === detection.type);
              if (!alreadyUsed) {
                this.$set(entry, 'assignedType', detection.type);
              }
            }
          });
        }
      } catch (e) {
        entries.forEach((entry) => { this.$set(entry, 'sheetsLoading', false); });
      }
    },

    removeFile(id) {
      this.uploadedFiles = this.uploadedFiles.filter(f => f.id !== id);
      if (!this.uploadedFiles.length) {
        this.validationResult = null;
        this.validationJobId = null;
        this.validationJob = null;
        this.message = '';
        this.clearPolling();
      }
    },

    availableTypes(currentId) {
      const usedTypes = new Set();
      this.uploadedFiles.forEach((f) => {
        if (f.id !== currentId && f.assignedType) {
          usedTypes.add(f.assignedType);
        }
      });
      return FILE_TYPES.filter(ft => !usedTypes.has(ft.value));
    },

    fileTypeLabel(type) {
      const found = FILE_TYPES.find(ft => ft.value === type);
      return found ? found.text : type;
    },

    // --- Validation ---

    async validateAll() {
      this.isValidating = true;
      this.message = '';
      this.clearPolling();
      this.validationResult = null;
      this.validationJobId = null;
      this.validationJob = null;
      this.uploadProgress = 0;

      const formData = new FormData();
      const baseUrl = localStorage.getItem('api_link');
      const apiToken = process.env.VUE_APP_API_TOKEN;

      // Only send validated file types to the validation endpoint
      this.uploadedFiles.forEach((entry) => {
        if (isValidatedType(entry.assignedType)) {
          formData.append(entry.assignedType, entry.file);
        }
      });

      try {
        const response = await axios.post(`${baseUrl}/validate/jobs`, formData, {
          headers: {
            'x-api-key': apiToken,
            'content-type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100), 10,
            );
          },
        });

        this.validationJobId = response.data.job_id;
        this.validationJob = {
          status: 'queued',
          message: response.data.message || 'Validation queued',
          progress_pct: 0,
          progress: {
            total_files: 0,
            total_sheets: 0,
            completed_sheets: 0,
            current_file: null,
            current_sheet: null,
          },
        };
        this.message = `Validation started (job ${this.validationJobId})`;
        await this.pollValidationStatus(baseUrl, apiToken);
        if (this.validationJob && (this.validationJob.status === 'queued' || this.validationJob.status === 'running')) {
          this.pollingTimer = setInterval(() => {
            this.pollValidationStatus(baseUrl, apiToken);
          }, 5000);
        }
      } catch (e) {
        // Fallback to synchronous endpoint
        try {
          const fallbackResponse = await axios.post(`${baseUrl}/validate/`, formData, {
            headers: {
              'x-api-key': apiToken,
              'content-type': 'multipart/form-data',
            },
          });
          if (fallbackResponse.data && typeof fallbackResponse.data === 'object' && fallbackResponse.data.files) {
            this.validationResult = fallbackResponse.data;
            this.message = '';
          } else {
            this.message = String(fallbackResponse.data);
            this.validationResult = null;
          }
        } catch (fallbackError) {
          this.validationResult = null;
          this.message = (fallbackError.response && fallbackError.response.data && fallbackError.response.data.message)
            ? fallbackError.response.data.message
            : 'Validation request failed';
        }
      } finally {
        this.isValidating = false;
      }
    },

    clearPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },

    async pollValidationStatus(baseUrl, apiToken) {
      if (!this.validationJobId) return;
      try {
        const response = await axios.get(`${baseUrl}/validate/jobs/${this.validationJobId}`, {
          headers: { 'x-api-key': apiToken },
        });
        this.validationJob = response.data;
        this.uploadProgress = Math.round(response.data.progress_pct || 0);

        if (response.data.status === 'done') {
          this.clearPolling();
          this.isValidating = false;
          if (response.data.result && response.data.result.files) {
            this.validationResult = response.data.result;
            this.message = '';
          } else {
            this.message = response.data.message || 'Validation completed';
          }
        } else if (response.data.status === 'error') {
          this.clearPolling();
          this.isValidating = false;
          this.message = response.data.message || 'Validation failed';
        }
      } catch (error) {
        this.clearPolling();
        this.isValidating = false;
        const apiMessage = error.response && error.response.data && error.response.data.message;
        this.message = apiMessage
          ? `Failed to refresh validation status: ${apiMessage}`
          : 'Failed to refresh validation status';
      }
    },

    // --- Planning creation ---

    async createPlanning() {
      if (!this.$refs.planningForm.validate()) return;
      this.creatingPlanning = true;

      // Map assigned types to File objects
      const fileMap = {};
      this.uploadedFiles.forEach((entry) => {
        if (entry.assignedType) {
          fileMap[entry.assignedType] = entry.file;
        }
      });

      try {
        const result = await createNewPlanning(
          this.planningInfo,
          this.username,
          this.date,
          this.lcyOption,
          this.sales_floor_id,
          this.funding_floor_id,
          this.sales_down_payment_id,
          this.fee_calculation_ol,
          this.fee_calculation_fl,
          this.rent_calculation_ol,
          this.rent_calculation_fl,
          this.depo_calculation_ol,
          this.depo_calculation_fl,
          fileMap.fa_import_file || new File([], ''),
          fileMap.fa_support_file || new File([], ''),
          fileMap.fa_rates_file || new File([], ''),
          fileMap.fa_new_planning_file || new File([], ''),
          fileMap.loans_planning_file || new File([], ''),
          fileMap.risk_planning_file || new File([], ''),
          fileMap.vendor_planning_file || new File([], ''),
          this.leasing_or_rent,
          this.other_funding_input,
          this.payment_choice_for_funding,
        );

        const { planningUser: user, planningId } = result;

        // If archive file was uploaded, import it via replacePlanning
        if (fileMap.archive_planning_file) {
          this.showSnackbar('Planning created. Importing archive data...', 'info');
          await replacePlanning(
            planningId,
            null,
            fileMap.archive_planning_file,
            null,
            null,
            null,
            null,
          );
        }

        this.showSnackbar('Planning created successfully! Calculation started.', 'success');
        await calcPlanning(user, planningId);

        this.$router.push({ name: 'Plannings' });
      } catch (err) {
        this.showSnackbar(err.message || 'Failed to create planning', 'error');
      } finally {
        this.creatingPlanning = false;
      }
    },

    // --- Helpers ---

    statusColor(status) {
      if (status === 'ok') return 'green';
      if (status === 'invalid') return 'orange darken-2';
      return 'red darken-2';
    },

    formatExamples(examples) {
      if (!examples || !examples.length) return '-';
      return examples
        .map(item => item.row)
        .filter(row => row !== null && row !== undefined)
        .join(', ');
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || 'success';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #bdbdbd;
  transition: border-color 0.2s, background-color 0.2s;
  cursor: pointer;
}
.drop-zone--active {
  border-color: #1976d2;
  background-color: #e3f2fd;
}
</style>
